import { createElement, Fragment } from 'react';
// cjs module
import { default as unified } from 'unified';
import remarkParse from 'remark-parse';
import remark2rehype from 'remark-rehype';
// If you need to support HTML tags, remember to sanitize the output
// https://github.com/remarkjs/remark-rehype#example-supporting-html-in-markdown-properly
import rehypeSanitize from 'rehype-sanitize';
import rehypeReact from 'rehype-react';

/**
 * Convert GFM pipe tables into HTML tables so Console content can use
 * standard markdown table syntax without remark-gfm.
 */
const convertPipeTablesToHtml = markdownText => {
  const lines = String(markdownText || '').split('\n');
  const output = [];
  let i = 0;

  const isTableRow = line => /^\s*\|.*\|\s*$/.test(line);
  const isSeparator = line =>
    /^\s*\|?(?:\s*:?-+:?\s*\|)+\s*:?-+:?\s*\|?\s*$/.test(line);
  const splitRow = line =>
    line
      .trim()
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .map(cell => cell.trim());

  while (i < lines.length) {
    if (isTableRow(lines[i]) && i + 1 < lines.length && isSeparator(lines[i + 1])) {
      const header = splitRow(lines[i]);
      i += 2;
      const body = [];
      while (i < lines.length && isTableRow(lines[i])) {
        body.push(splitRow(lines[i]));
        i += 1;
      }

      const th = header.map(cell => `<th>${cell}</th>`).join('');
      const tr = body
        .map(row => `<tr>${row.map(cell => `<td>${cell || ''}</td>`).join('')}</tr>`)
        .join('');
      output.push(`<table><thead><tr>${th}</tr></thead><tbody>${tr}</tbody></table>`);
      continue;
    }

    output.push(lines[i]);
    i += 1;
  }

  return output.join('\n');
};

const textNode = value => ({ type: 'text', value: value || '' });

const element = (tagName, children = [], properties = {}) => ({
  type: 'element',
  tagName,
  properties,
  children,
});

/**
 * Parse a simple HTML table string into a HAST element tree.
 * Supports thead/tbody/tr/th/td (enough for Flex Console legal pages).
 */
const htmlTableToHast = html => {
  const tableMatch = html.match(/<table[^>]*>([\s\S]*?)<\/table>/i);
  if (!tableMatch) {
    return null;
  }

  const tableHtml = tableMatch[1];
  const rows = [];
  const rowRegex = /<tr[^>]*>([\s\S]*?)<\/tr>/gi;
  let rowMatch;

  while ((rowMatch = rowRegex.exec(tableHtml)) !== null) {
    const cells = [];
    const cellRegex = /<(th|td)[^>]*>([\s\S]*?)<\/\1>/gi;
    let cellMatch;
    while ((cellMatch = cellRegex.exec(rowMatch[1])) !== null) {
      const tagName = cellMatch[1].toLowerCase();
      const content = cellMatch[2].replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '');
      cells.push(element(tagName, [textNode(content)]));
    }
    if (cells.length) {
      rows.push({ isHeader: cells.every(c => c.tagName === 'th'), cells });
    }
  }

  if (!rows.length) {
    return null;
  }

  const headerRows = rows.filter(r => r.isHeader);
  const bodyRows = rows.filter(r => !r.isHeader);
  const children = [];

  if (headerRows.length) {
    children.push(
      element(
        'thead',
        headerRows.map(r => element('tr', r.cells))
      )
    );
  }

  if (bodyRows.length || !headerRows.length) {
    const rowsForBody = bodyRows.length ? bodyRows : rows;
    children.push(
      element(
        'tbody',
        rowsForBody.map(r => element('tr', r.cells))
      )
    );
  }

  return element('table', children);
};

/**
 * Turn raw/html table nodes (from allowDangerousHtml) into real element nodes
 * so rehype-react can render them and our CSS can style them.
 */
const rehypeParseTables = () => tree => {
  const walk = (node, index, parent) => {
    if (!node) {
      return;
    }

    if (
      (node.type === 'raw' || node.type === 'element') &&
      typeof node.value === 'string' &&
      /<table[\s>]/i.test(node.value) &&
      parent &&
      typeof index === 'number'
    ) {
      const table = htmlTableToHast(node.value);
      if (table) {
        parent.children[index] = table;
        return;
      }
    }

    // remark may keep HTML as mdast html → hast raw with value
    if (node.type === 'raw' && typeof node.value === 'string' && /<table[\s>]/i.test(node.value)) {
      const table = htmlTableToHast(node.value);
      if (table && parent && typeof index === 'number') {
        parent.children[index] = table;
        return;
      }
    }

    if (Array.isArray(node.children)) {
      // Iterate copy of indices because we replace in place
      for (let i = 0; i < node.children.length; i++) {
        walk(node.children[i], i, node);
      }
    }
  };

  walk(tree, null, null);
};

const processor = (components = {}) => {
  return unified()
    .use(remarkParse)
    .use(remark2rehype, { allowDangerousHtml: true })
    .use(rehypeParseTables)
    .use(rehypeSanitize)
    .use(rehypeReact, {
      createElement,
      Fragment,
      components,
    });
};

const renderMarkdown = (markdownText, components) => {
  const withTables = convertPipeTablesToHtml(markdownText);
  return processor(components).processSync(withTables).result;
};
export default renderMarkdown;
